import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { auth, db } from "./firebase/firebase"; // adjust path if needed
import { updateProfile } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";

const emblems = [
  "13guest.png", "1jester.png", "2recruit.png", "3lonewolf.png", "4steed.png",
  "5sniper.png", "6lionheart.png", "7spartan.png", "8warelephant.png", "9kingofthesea.png",
  "10nuclear.png", "11wargod.png", "12omnipotent.png"
];

// score threshold → title (descending)
const titleScale = [
  { min: 100, label: "the Chronologicle" },
  { min: 100, label: "the Omniscient" },
  { min: 95, label: "the Legend" },
  { min: 90, label: "the Genius" },
  { min: 85, label: "the Master" },
  { min: 80, label: "the Wise" },
  { min: 75, label: "the Expert" },
  { min: 70, label: "the Veteran" },
  { min: 65, label: "the Scholar" },
  { min: 60, label: "the Practical" },
  { min: 55, label: "the Apprentice" },
  { min: 50, label: "the Bookworm" },
  { min: 45, label: "the Explorer" },
  { min: 40, label: "the Nomad" },
  { min: 35, label: "the Wanderer" },
  { min: 30, label: "the Vagabond" },
  { min: 25, label: "the Amateur" },
  { min: 20, label: "the Novice" },
  { min: 15, label: "the Guesser" },
  { min: 10, label: "the Clueless" },
  { min: 5, label: "the Hopeless" },
  { min: 0, label: "the Forgotten" },
];

const flatTitles = titleScale.map(t => t.label);

const ProfileMenu = ({
  displayName,
  setDisplayName,
  onClose,
  equippedEmblem,
  setEquippedEmblem,
  equippedTitle,
  setEquippedTitle,
  testData = null,
}) => {
  const [, setFocusedEmblemIndex] = useState(0);
  const [, setFocusedTitleIndex] = useState(0);
  const [focusMode, setFocusMode] = useState(null);
  const [activeSection, setActiveSection] = useState(() => testData?.activeSection ?? "name");
  const [tempEmblem, setTempEmblem] = useState(equippedEmblem);
  const [tempTitle, setTempTitle] = useState(equippedTitle);
  const [tempDisplayName, setTempDisplayName] = useState(displayName);

  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 600px)").matches;

  useEffect(() => {
    if (testData) return undefined;

    const initialEmblem = localStorage.getItem("equippedEmblem") || "13guest.png";
    const initialTitle = localStorage.getItem("equippedTitle") || "the Chronologicle";
    setTempEmblem(initialEmblem);
    setTempTitle(initialTitle);
    setFocusedEmblemIndex(emblems.indexOf(initialEmblem));
    setFocusedTitleIndex(flatTitles.indexOf(initialTitle));
  }, [testData]);

  useEffect(() => {
    const TITLES_PER_ROW = 1;
    const handleKeyDown = (e) => {
      if (focusMode === "emblem") {
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          setFocusedEmblemIndex((prev) => {
            const newIndex = (prev + (e.key === "ArrowLeft" ? -1 : 1) + emblems.length) % emblems.length;
            setTempEmblem(emblems[newIndex]);
            return newIndex;
          });
        }
      } else if (focusMode === "title") {
        if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
          setFocusedTitleIndex((prev) => {
            let delta = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : e.key === "ArrowUp" ? -TITLES_PER_ROW : TITLES_PER_ROW;
            const newIndex = (prev + delta + flatTitles.length) % flatTitles.length;
            setTempTitle(flatTitles[newIndex]);
            return newIndex;
          });
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusMode]);

  useEffect(() => {
    if (activeSection === "emblem") setFocusMode("emblem");
    else if (activeSection === "title") setFocusMode("title");
    else setFocusMode(null);
  }, [activeSection]);

  const saveChanges = async () => {
    setEquippedEmblem(tempEmblem);
    setEquippedTitle(tempTitle);
    setDisplayName(tempDisplayName);
    localStorage.setItem("equippedEmblem", tempEmblem);
    localStorage.setItem("equippedTitle", tempTitle);
    localStorage.setItem("displayName", tempDisplayName);

    const user = auth.currentUser;
    if (user) {
      try {
        await updateProfile(user, { displayName: tempDisplayName });
        await setDoc(
          doc(db, "users", user.uid),
          { displayName: tempDisplayName, emblem: tempEmblem, title: tempTitle },
          { merge: true }
        );
      } catch (err) {
        console.error("Failed to save display name to Firebase:", err);
      }
    }
    onClose();
  };

  const renderSection = () => {
    if (activeSection === "display") {
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} style={{ marginTop: "1.5rem" }}>
          <input
            type="text"
            value={tempDisplayName}
            onChange={(e) => setTempDisplayName(e.target.value)}
            maxLength={20}
            placeholder="Your name"
            style={{
              fontSize: "1.25rem",
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              border: "1px solid #ebcf94",
              background: "#111",
              color: "#e7c17f",
              fontFamily: "'VT323', monospace",
              textAlign: "center"
            }}
          />
        </motion.div>
      );
    }
    if (activeSection === "emblem") {
      // Rows: 2, 4, 4, 4
      const emblemRows = [
        emblems.slice(0, 1),   // Guest
        emblems.slice(1, 5),   // Level 1
        emblems.slice(5, 9),  // Level 2
        emblems.slice(9, 13), // Level 3
      ];

      // Tier background colors for each emblem in that row
      const tierBg = ["#176d58", "#045340", "#00241e", "#000000"];

      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.75rem",
            width: "100%",

          }}
        >
          {emblemRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "1rem",
                flexWrap: "nowrap",

              }}
            >
              {row.map((e) => (
                <img
                  key={e}
                  src={`/emblems/${e}`}
                  alt={e}
                  onClick={() => {
                    setTempEmblem(e);
                    setFocusMode("emblem");
                    setFocusedEmblemIndex(emblems.indexOf(e));
                  }}
                  style={{
                    width: isMobile ? "5rem" : "5rem",
                    height: isMobile ? "5rem" : "5rem",
                    background: tierBg[rowIndex], // bg sits inside the border
                    border: tempEmblem === e ? "3.5px solid #ebcf94" : "3.5px solid transparent",
                    borderRadius: ".75rem",
                    boxSizing: "border-box",      // keep total size incl. border
                    cursor: "pointer",
                    display: "block",
                    transition: "transform 0.2s ease",

                  }}
                />
              ))}
            </div>
          ))}
        </motion.div>
      );
    }
    if (activeSection === "title") {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ layout: { duration: 0.02 }, opacity: { duration: 0.2 } }}
          layout
          style={{ width: "100%" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.6rem",
              alignItems: "center",
              width: "100%",
            }}
          >
            {titleScale.map(({ min, label }, idx) => (
              <button
                key={label}
                onClick={() => {
                  setTempTitle(label);
                  setFocusMode("title");
                  setFocusedTitleIndex(idx);
                }}
                style={{
                  width: "min(520px, 100%)",
                  padding: isMobile ? "0.5rem 0.8rem" : "0.6rem 1rem",
                  background: tempTitle === label ? "#ebcf94" : "#082f26",
                  color: tempTitle === label ? "#4b2e00" : "#e7c17f",
                  border: tempTitle === label ? "2px solid #ebcf94" : "1px solid #928773",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontFamily: "'VT323', monospace",
                  fontSize: isMobile ? "1rem" : "1.05rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ opacity: 0.75 }}>{min}</span>
                <span>{label}</span>
              </button>
            ))}
          </div>
        </motion.div>
      );
    }
    return null;
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        width: "auto",
        height: isMobile ? "100dvh" : "100vh",
        background: "radial-gradient(circle at center, #03715f, #034c39)",
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        color: "#e7c17f",
        fontFamily: "'Baskerville', serif",
        boxSizing: "border-box",
      }}
    >
      {/* 🔝 Top tabs (stuck at the very top) */}
      <div
        style={{
          position: "sticky",     // harmless since the container itself doesn't scroll
          top: 0,
          zIndex: 101,
          padding: "0.75rem 1rem",

        }}
      >
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            width: "100%",
            maxWidth: "1000px",
            margin: ".75rem auto",
            flexWrap: "nowrap",
          }}
        >
          {[
            { label: "Edit Name", value: "name" },
            { label: "Edit Emblem", value: "emblem" },
            { label: "Edit Title", value: "title" },
          ].map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setActiveSection(value)}
              style={{
                flex: "1 1 0%",
                minWidth: 0,
                height: "52px",
                padding: "0 1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                borderRadius: "12px",
                fontWeight: "bold",
                fontSize: "1.1rem",
                fontFamily: "'Baskerville', serif",  // ← same font as your UI
                background: activeSection === value ? "#ebcf94" : "#034c39",
                color: activeSection === value ? "#4b2e00" : "#e7c17f",
                transition: "transform 0.15s, filter 0.15s",
              }}
              aria-pressed={activeSection === value}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          maxWidth: "1000px",
          width: "100%",
          margin: "0 auto",
          padding: "0 1rem",
          boxSizing: "border-box",

          // ⬇️ Center the active section between the sticky tabs and sticky footer
          display: "grid",
          placeItems: "center",

          // Safety: if any section *does* run tall, let it scroll instead of
          // spilling under the footer. Titles are small enough that this
          // usually won’t kick in, but it’s a nice fallback.
          overflowY: "auto",
          // Give a little breathing room above/below for iOS safe areas, etc.
          paddingTop: "calc(0.75rem + env(safe-area-inset-top))",
          paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
        }}
      >
        {/* Centered content wrapper */}
        <div style={{ width: "100%" }}>
          {activeSection === "name" && (
            <div style={{ textAlign: "center" }}>
              <input
                type="text"
                value={tempDisplayName}
                onChange={(e) => setTempDisplayName(e.target.value)}
                maxLength={20}
                placeholder="Enter name"
                style={{
                  fontSize: isMobile ? "2.5rem" : "2rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "12px",
                  border: "2px solid #e7c17f",
                  background: "#082f26",
                  color: "#e7c17f",
                  fontFamily: "'Baskerville', serif",
                  fontWeight: "bold",
                  textAlign: "center",
                  outline: "none",
                  height: "6rem",                // allow it to adapt
                  width: "100%",                  // fill the container
                  maxWidth: "520px",              // prevent it from being huge on desktop
                  boxSizing: "border-box",        // include padding in width calc
                  margin: "0 auto",
                  display: "block",
                }}
              />
            </div>
          )}

          {renderSection()}
        </div>
      </div>

      {/* Sticky footer area: section tabs row above Cancel/Save row */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "0.75rem 1rem",
          paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
        }}
      >
        <motion.div
          transition={{ duration: 0.15 }}
          layout
          style={{
            display: "flex",
            flexDirection: "column",  // stack tabs row above buttons row
            gap: "0.75rem",
            maxWidth: "1000px",
            width: "100%",
            margin: ".75rem auto",
          }}
        >

          {/* Action row: Cancel (left) + Save (right) */}
          <div style={{ display: "flex", gap: "0.75rem", width: "100%" }}>
            <button
              onClick={onClose}
              style={{
                boxShadow: "none",
                border: "none",
                fontSize: "1.5rem",
                color: "#e7c17f",
                background: "#0d3b2f",
                borderRadius: "12px",
                padding: "1rem 1rem",
                fontWeight: "bold",
                flex: 1,
              }}
              className="submit-button"
            >
              Cancel
            </button>

            <button
              onClick={saveChanges}
              style={{
                boxShadow: "none",
                border: "none",
                fontSize: "1.5rem",
                color: "#e7c17f",
                background: "#034c39",
                borderRadius: "12px",
                padding: "1rem 1rem",
                fontWeight: "bold",
                flex: 1,
              }}
              className="submit-button"
            >
              Save
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfileMenu;
